import { useState, useRef } from "react"
import classNames from "classnames"
import { get } from "lodash"
import { Box, TextField, IconButton, ClickAwayListener } from "@mui/material"
import { Clear, Search as SearchIcon } from "@mui/icons-material"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import UnifiedSearch from "modules/unified-search"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { BASKET, ETF, PORTFOLIO, BENCHMARK, INDEX, SWAP } from "connected-components/compare/position-sets"
import { Uuid } from "helpers/types/uuid"
import { BaseSelectorProps, SearchItemType, SearchResultItem, ItemAction } from "./types"
import { BaseInstrumentWithName } from "connected-components/optimize-experiments/types"

import "./style.scss"

const allowedTypes: SearchItemType[] = ["benchmark", "index", "portfolio", "swap", "security", "experiment"]

function filterEquitySearchResults(value: SearchResultItem): boolean {
  return get(value, "assetClass") !== "equity" || get(value, "assetSubclass") === "etf"
}

function disableResult(item: SearchResultItem, instrument: SelectedTypedInstrument | null): boolean {
  const isNonEtfEquity = get(item, "assetClass") === "equity" && get(item, "assetSubclass") !== "etf"
  const isMainInstrument = instrument && item?.id && item.id === instrument.id

  return Boolean(isNonEtfEquity || isMainInstrument)
}

/**
 * Transform a SearchResultItem from UnifiedSearch into a BaseInstrumentWithName
 * This handles the type conversion from lowercase search types to uppercase SelectedTypedInstrument types
 */
function transformSearchResultToBase(item: SearchResultItem): BaseInstrumentWithName {
  const { id, name, type, experimentId } = item

  if (!id || !type) {
    throw new Error("SearchResultItem must have id and type")
  }

  const displayName = name || id

  // Handle each type specifically to satisfy TypeScript's union type requirements
  switch (type) {
    case "portfolio":
      if (experimentId) {
        // This is an experiment (portfolio with experimentId)
        return { type: PORTFOLIO, id: id as Uuid, experimentId, name: displayName }
      }
      return { type: PORTFOLIO, id: id as Uuid, name: displayName }

    case "experiment":
      // Experiments are portfolios with experimentId
      return { type: PORTFOLIO, id: id as Uuid, experimentId: experimentId || id, name: displayName }

    case "benchmark":
      return { type: BENCHMARK, id, name: displayName }

    case "index":
      return { type: INDEX, id, name: displayName }

    case "swap":
      return { type: SWAP, id, name: displayName }

    case "security":
      // Securities default to ETF (swaps are handled separately in itemsActions)
      return { type: ETF, id, name: displayName }

    default: {
      // Fallback: handle any unexpected types
      throw new Error(`Unknown instrument type: ${type}`)
    }
  }
}

/**
 * BaseSelector Component - TextField-like base selector
 *
 * Three states that all look like TextFields:
 * 1. None - Shows "None" (can override inherited base)
 * 2. Search - Shows UnifiedSearch in TextField style
 * 3. Selected - Shows selected base in TextField style
 *
 */
export default function BaseSelector({
  account,
  modelId,
  instrument,
  baseInstrument,
  handleInstrumentSelected,
  label = "Benchmark",
  allowNone = true,
  disabled = false,
}: BaseSelectorProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(true) // Start as focused since we autoFocus
  const fieldRef = useRef<HTMLDivElement>(null)

  const displayName = baseInstrument?.name || baseInstrument?.id || ""

  const handleClear = () => {
    setIsSearchOpen(false)
    handleInstrumentSelected(null)
  }

  const handleClickAway = () => {
    if (isSearchOpen) {
      setIsSearchOpen(false)
    }
  }

  // Create itemsActions for UnifiedSearch
  const createPassthroughAction = (): ItemAction => ({
    noDispatch: true,
    mainAction: (selectedItem: SearchResultItem) => () => {
      const transformed = transformSearchResultToBase(selectedItem)
      handleInstrumentSelected(transformed)
      setIsSearchOpen(false)
    },
  })

  const itemsActions: Record<string, ItemAction> = {
    security: {
      noDispatch: true,
      mainAction: (selectedItem: SearchResultItem) => () => {
        // Security type is either swap or ETF (other equities are filtered out)
        // For securities, we need to use specific id fields and set correct type
        const isSwap = selectedItem.assetClass === "swap"
        const transformedItem: BaseInstrumentWithName = {
          type: isSwap ? BASKET : ETF,
          id: isSwap ? (selectedItem.ticker || selectedItem.id || "") : (selectedItem.internalId || selectedItem.id || ""),
          name: selectedItem.name || selectedItem.ticker || selectedItem.id || "",
        }
        handleInstrumentSelected(transformedItem)
        setIsSearchOpen(false)
      },
    },
    portfolio: createPassthroughAction(),
    benchmark: createPassthroughAction(),
    index: createPassthroughAction(),
    experiment: createPassthroughAction(),
  }

  // Search mode
  if (isSearchOpen) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box
          className={classNames("components_base-field", "search-mode", { focused: isFocused })}
          ref={fieldRef}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        >
          <Box className="search-container">
            <span className="search-label">
              {label}
            </span>
            <UnifiedSearch
              account={account}
              modelId={modelId}
              isOpen={true}
              isDisabled={disabled || !modelId}
              disableResult={(item: SearchResultItem) => disableResult(item, instrument)}
              searchType={RECENT_INSTRUMENT_SEARCH_TYPES.BASE}
              filterEquities={filterEquitySearchResults}
              placeholder="Search..."
              allowedTypes={allowedTypes}
              itemsActions={itemsActions}
              variant="outlined"
              autoFocus={true}
            />
          </Box>
        </Box>
      </ClickAwayListener>
    )
  }

  // None selected
  if (!baseInstrument && allowNone) {
    return (
      <Box className="components_base-field">
        <TextField
          fullWidth
          label={label}
          value="None"
          onClick={() => !disabled && setIsSearchOpen(true)}
          variant="outlined"
          size="small"
          disabled={disabled}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: !disabled ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsSearchOpen(true)
                  }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              ) : undefined,
            },
          }}
        />
      </Box>
    )
  }

  // Base selected
  return (
    <Box className="components_base-field">
      <TextField
        fullWidth
        label={label}
        value={displayName}
        onClick={() => !disabled && setIsSearchOpen(true)}
        variant="outlined"
        size="small"
        disabled={disabled}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: !disabled ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            ) : undefined,
          },
        }}
      />
    </Box>
  )
}
